import React, { FC } from 'react';
import './Pagination.scss';
import { image } from '../../../app/utils/common/image';

interface PaginationProps {
    pageNumbers: number[];
    handlePageLink: (page: any) => void;
    prevPage: () => void;
    nextPage: () => void;
    totalProfile: number;
    activePage: number;
    currentTotal: number;
}

const getPaginatedNumbers = (pageNumbers: number[], activePage: number, maxVisible: number = 6) => {
    const total = pageNumbers.length;

    if (total <= maxVisible) {
        return pageNumbers;
    }

    const half = Math.floor(maxVisible / 2);
    let start = Math.max(activePage - half, 1);
    let end = Math.min(activePage + half, total);

    if (activePage - half < 1) {
        end = Math.min(maxVisible, total);
    } else if (activePage + half > total) {
        start = Math.max(total - maxVisible + 1, 1);
    }

    const pages: (number | string)[] = pageNumbers.slice(start - 1, end);

    if (start > 1) {
        pages.unshift('prev-dots');
    }
    if (end < total) {
        pages.push('next-dots');
    }

    return pages;
};

const Pagination: FC<PaginationProps> = (props) => {
    const { pageNumbers, handlePageLink, prevPage, nextPage, totalProfile, activePage, currentTotal } = props;
    const isPrevDisabled = activePage <= 1;
    const isNextDisabled = activePage >= pageNumbers.length;

    const remainingPageNumbers = getPaginatedNumbers(pageNumbers, activePage);

    return (
        <div className='pagination-container'>
            <div className='total-pagination-no'> Showing {currentTotal} from {totalProfile} data</div>
            <nav className='list-disabled'>
                <ul className='pagination-list'>

                    {/* Previous Button */}
                    <li
                        className={`prev-page-button ${isPrevDisabled ? 'disabled' : ''}`}
                        onClick={!isPrevDisabled ? prevPage : undefined}
                    >
                        <div>
                            <img src={image.PREV_ICON} alt="Previous Icon" />
                            <img src={image.PREV_LIGHT_ICON} alt="Previous Light Icon" />
                        </div>
                        <div>Previous</div>
                    </li>

                    {/* Page Number Buttons */}
                    {remainingPageNumbers.map((number, index) => {
                        if (number === 'prev-dots') {
                            return (
                                <button
                                    key={`prev-dots-${index}`}
                                    onClick={() => handlePageLink(Math.max(activePage - 5, 1))} // Jump back 5 pages or to the first page
                                    className="pagination-paper"
                                >
                                    ...
                                </button>
                            );
                        }
                        if (number === 'next-dots') {
                            return (
                                <button
                                    key={`next-dots-${index}`}
                                    onClick={() => handlePageLink(Math.min(activePage + 5, pageNumbers.length))} // Jump forward 5 pages or to the last page
                                    className="pagination-paper"
                                >
                                    ...
                                </button>
                            );
                        }
                        return (
                            <button
                                key={number}
                                onClick={() => handlePageLink(number)}
                                className={`pagination-paper ${activePage === number ? 'pagination-paper-inactive' : ''}`}
                            >
                                {number}
                            </button>
                        );
                    })}

                    {/* Next Button */}
                    <li
                        className={`prev-page-button ${isNextDisabled ? 'disabled' : ''}`}
                        onClick={!isNextDisabled ? nextPage : undefined}
                    >
                        <div>Next</div>
                        <div>
                            <img src={image.PREV_ICON} style={{ transform: 'scaleX(-1)' }} alt="Next Icon" />
                            <img src={image.PREV_LIGHT_ICON} style={{ transform: 'scaleX(-1)' }} alt="Next Light Icon" />
                        </div>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default Pagination;
